import { ApeironBlueButton, theme } from '@apeiron/temp'
import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import BannerPNG from '@public/images/image-battle-demo-vip-pass-banner.png'
import MobileBannerPNG from '@public/images/image-battle-demo-vip-pass-banner-mobile.png'
import ENV_CONFIG from '@src/config'
import { useVipPassContext } from '@src/contexts/battle/demo/VipPassContext'
import { format, parseISO } from 'date-fns'
import * as R from 'ramda'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  background-size: cover;

  ${props => props.theme.breakpoints.up('res1024')} {
    aspect-ratio: 1250 / 384;
    max-width: 1250px;
    height: 384px;
    background-image: url(${BannerPNG.src});
    margin: 20px;
    width: 95%;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    aspect-ratio: 748 / 390;
    height: 390px;
    background-image: url(${MobileBannerPNG.src});
    background-position: right;
    width: 100%;
    padding: 10px;
  }
`

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;

  ${props => props.theme.breakpoints.up('res1024')} {
    width: 350px;
    gap: 10px;
    margin: 30px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    width: 100%;
    gap: 20px;
  }
`

const Title = styled(Typography)`
  font-weight: 700;
  line-height: 80%;
  color: #67fffe;

  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 90px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 80px;
  }

  ${props => props.theme.breakpoints.down('res768')} {
    font-size: 50px;
  }
`

const DateContainer = styled(Box)`
  background-color: #000000;
  border-radius: 20px;
  padding: 2px 24px;
  width: fit-content;
  min-width: 300px;
`

const Dates = styled(Typography)`
  display: flex;
  justify-content: center;

  line-height: 32px;
  color: #ffb821;

  ${props => props.theme.breakpoints.up('res1024')} {
    font-size: 20px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    font-size: 24px;
  }
`

const BlueButton = styled(ApeironBlueButton)`
  width: 350px;
  height: 70px;
  border-radius: 2px;

  font-size: 26px;
  font-weight: 700;
  line-height: 1;
`

const BottomText = styled(Typography)`
  line-height: 1;

  ${props => props.theme.breakpoints.up('res1024')} {
    color: #000000;
    font-weight: 700;
    font-size: 32px;
    padding-left: 24px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    margin: 20px 0px;
    color: #ffffff;
    font-weight: 400;
    font-size: 36px;
  }
`

const VipPassBanner = () => {
  const { t } = useTranslation()

  const { currentBattlePass } = useVipPassContext()

  const mobile = useMediaQuery(theme.breakpoints.down('res1024'))

  const handleToWebstore = useCallback(() => {
    window.open(ENV_CONFIG.EXTERNAL_URL.WEBSTORE.VIP_SHOP, '_blank')
  }, [])

  const formatDate = useCallback((date: string) => {
    return format(parseISO(date), 'dd/M HH:mm')
  }, [])

  const dates = useMemo(() => {
    if (R.isNil(currentBattlePass)) return null

    const startTime = formatDate(currentBattlePass.startAt)

    const endTime = formatDate(currentBattlePass.endAt)

    return `${startTime} - ${endTime}`
  }, [currentBattlePass, formatDate])

  return (
    <MainContainer>
      {mobile && <BottomText>{t('vip_pass.banner.bottom_text')}</BottomText>}

      <ContentContainer>
        <Title>{t('vip_pass.banner.title')}</Title>

        <DateContainer>
          <Dates>{dates || t('vip_pass.banner.stay_tune')}</Dates>
        </DateContainer>

        <BlueButton onClick={handleToWebstore}>
          {t('vip_pass.banner.button')}
        </BlueButton>
      </ContentContainer>

      {!mobile && <BottomText>{t('vip_pass.banner.bottom_text')}</BottomText>}
    </MainContainer>
  )
}

export default VipPassBanner
